// The chart body's boundary (TODO §15/§3.2, §4): seeding, the table edits the Data view
// makes, clipboard parsing, the row cap, and the projection from `{columns, rows, encoding}`
// into the row shape Recharts consumes.
//
// Deliberately free of any runtime import from `recharts`: `ContentWindow` calls
// `createChartDoc` on the "Add chart" click, and a value import here would drag the library
// into the main bundle for every deck that never draws one (§2.4/§7).

import type {
  ChartColumn,
  ChartDocument,
  ChartRow,
  ChartSeries,
  ChartType,
} from "@/types/chart";

// §3.2.2 A slide chart never legitimately needs more, and the persistence cost is real — the
// whole document is rewritten on the debounce (§3.3.3). Enforced at IMPORT, where it is
// reported rather than applied silently (§4.5): a truncation nobody is told about is a lie
// about the data on the slide.
export const CHART_ROW_CAP = 5000;

// §5.3.3 The floor on an interval refresh. Below this the editor is hammering a source for a
// chart nobody is watching change.
export const CHART_MIN_INTERVAL_MINUTES = 5;

// §5.3.2 `on-open` skips the fetch if the cached rows are younger than this, so tab-switching
// between decks does not re-request on every open.
export const CHART_STALENESS_FLOOR_MS = 60_000;

// === Number formats ========================================================================
//
// A KEY on the doc rather than a stored `Intl.NumberFormatOptions` object (§3.2), so the
// stored shape cannot drift from what `Intl` accepts and there is no second thing to migrate.
// `auto` is not a format at all — it is "let the value print itself", which is right for the
// ids and counts most tables hold.
export const NUMBER_FORMATS: Record<string, Intl.NumberFormatOptions | null> = {
  auto: null,
  integer: { maximumFractionDigits: 0 },
  decimal1: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  decimal2: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  thousands: { notation: "compact", compactDisplay: "short" },
  percent: { style: "percent", maximumFractionDigits: 1 },
  currency: { style: "currency", currency: "EUR", maximumFractionDigits: 0 },
};

export const NUMBER_FORMAT_LABELS: Record<string, string> = {
  auto: "Automatic",
  integer: "1234",
  decimal1: "1234.5",
  decimal2: "1234.56",
  thousands: "1.2K",
  percent: "12.3%",
  currency: "€1,234",
};

// One formatter per key, built lazily and reused: `Intl.NumberFormat` construction is not
// cheap and a chart calls this once per tick, per label and per tooltip row.
const formatters = new Map<string, Intl.NumberFormat | null>();

export function formatNumber(value: unknown, formatKey?: string): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return value == null ? "" : String(value);
  }
  const key = formatKey && formatKey in NUMBER_FORMATS ? formatKey : "auto";
  if (!formatters.has(key)) {
    const options = NUMBER_FORMATS[key];
    formatters.set(
      key,
      options ? new Intl.NumberFormat(undefined, options) : null,
    );
  }
  const formatter = formatters.get(key);
  return formatter ? formatter.format(value) : String(value);
}

// === Cell values ===========================================================================

// A pasted or typed cell → the stored value. Numbers are stored as numbers so the chart can
// plot them and the axis can scale; everything else stays a string.
//
// The blank case is the one that matters: an empty cell is `null`, NOT `0`. A gap in a series
// and a zero are different claims about the data, and collapsing them draws a line down to
// the axis for a month nobody measured.
export function parseCell(raw: string): string | number | null {
  const text = raw.trim();
  if (text === "") return null;
  // A plain decimal only. Deliberately narrow: `Number("0x1f")`, `Number("1e5")` and
  // `Number(" ")` all succeed, and a product code like "1e5" silently becoming 100000 is the
  // kind of data corruption nobody notices until the slide is on a screen.
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
  return text;
}

// The stored value → what the Data view puts in the input. `null` is the empty string, so an
// empty cell reads as empty rather than as the word "null".
export function cellText(value: string | number | null | undefined): string {
  return value == null ? "" : String(value);
}

// A column's type, INFERRED from the values in it — used when importing, where nobody has
// declared one. Numeric wins only if every non-blank cell is numeric: one stray "n/a" in a
// column of numbers makes it text, which is the safe direction (a text column simply cannot
// be plotted, whereas a numeric column with a text value plots a hole).
export function inferColumnType(values: unknown[]): ChartColumn["type"] {
  let seen = false;
  for (const value of values) {
    if (value == null || value === "") continue;
    seen = true;
    if (typeof value !== "number") return "text";
  }
  return seen ? "number" : "text";
}

// === Clipboard =============================================================================

// §4.2 A clipboard paste → a rectangular table. Accepts what Excel, Sheets and a Gridd all
// put on the clipboard as `text/plain`: tab-separated by preference, comma-separated when the
// text holds no tabs at all.
//
// **Quotes are handled for the comma case and not for the tab case, on purpose.** A TSV cell
// cannot contain a tab, so splitting is unambiguous and a value like `"Smith, J"` must keep
// its quotes if the user typed them. A CSV cell can contain a comma, so the quoted form has
// to be understood or every address column arrives shifted by one.
export function parseClipboardTable(text: string): string[][] {
  const clean = text.replace(/\r\n?/g, "\n").replace(/\n+$/, "");
  if (clean === "") return [];
  const lines = clean.split("\n");
  if (clean.includes("\t")) return lines.map((line) => line.split("\t"));
  return lines.map(splitCsvLine);
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      // A doubled quote inside a quoted cell is one literal quote — RFC 4180's escape, and
      // what every spreadsheet emits.
      if (ch === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"' && cell === "") {
      quoted = true;
    } else if (ch === ",") {
      out.push(cell);
      cell = "";
    } else {
      cell += ch;
    }
  }
  out.push(cell);
  return out;
}

// === Table edits ===========================================================================
//
// Every one returns a NEW document. The Data view dispatches `chartDocUpdated` with whatever
// comes back, so these are the whole of the table's edit vocabulary and none of them mutates.

export function newColumn(label: string): ChartColumn {
  return { id: crypto.randomUUID(), label, type: "text" };
}

// A row with every column present and blank. Building it from the columns rather than `{}`
// keeps the Data view's inputs controlled from the first render.
export function blankRow(columns: ChartColumn[]): ChartRow {
  const row: ChartRow = {};
  for (const column of columns) row[column.id] = null;
  return row;
}

export function setCell(
  doc: ChartDocument,
  rowIndex: number,
  columnId: string,
  raw: string,
): ChartDocument {
  const rows = doc.rows.map((row, i) =>
    i === rowIndex ? { ...row, [columnId]: parseCell(raw) } : row,
  );
  // A cell edit is a MANUAL edit, whatever the rows originally came from — so the source
  // stops claiming to be a CSV import or a live URL. Leaving it would mean the next refresh
  // silently threw the user's correction away with no warning that it was going to.
  return { ...doc, rows, source: demoteSource(doc) };
}

// A typed edit demotes a derived source to `manual`, for the reason above. A doc already
// manual is returned by reference so an edit does not churn the source object.
function demoteSource(doc: ChartDocument): ChartDocument["source"] {
  return doc.source.kind === "manual" ? doc.source : { kind: "manual" };
}

export function addRow(doc: ChartDocument): ChartDocument {
  return {
    ...doc,
    rows: [...doc.rows, blankRow(doc.columns)],
    source: demoteSource(doc),
  };
}

export function removeRow(doc: ChartDocument, index: number): ChartDocument {
  return {
    ...doc,
    rows: doc.rows.filter((_, i) => i !== index),
    source: demoteSource(doc),
  };
}

export function moveRow(
  doc: ChartDocument,
  index: number,
  delta: number,
): ChartDocument {
  const target = index + delta;
  if (target < 0 || target >= doc.rows.length) return doc;
  const rows = [...doc.rows];
  [rows[index], rows[target]] = [rows[target], rows[index]];
  return { ...doc, rows, source: demoteSource(doc) };
}

export function addColumn(doc: ChartDocument): ChartDocument {
  const column = {
    ...newColumn(`Series ${doc.columns.length}`),
    type: "number" as const,
  };
  return {
    ...doc,
    columns: [...doc.columns, column],
    rows: doc.rows.map((row) => ({ ...row, [column.id]: null })),
    // A new numeric column is plotted immediately — an author who adds one wants to see it,
    // and making them also find it in the panel is a step with no decision in it.
    encoding: {
      ...doc.encoding,
      series: [...doc.encoding.series, { columnId: column.id }],
    },
    source: demoteSource(doc),
  };
}

// Removing a column takes its cells, its series and — if it was the X — the encoding's X with
// it, repointing at the first surviving column. A dangling `encoding.x` renders an empty
// chart, and "I deleted a column and the whole chart went blank" is not a thing to leave
// possible.
export function removeColumn(
  doc: ChartDocument,
  columnId: string,
): ChartDocument {
  const columns = doc.columns.filter((c) => c.id !== columnId);
  if (columns.length === 0) return doc; // never leave a table with no columns at all
  const rows = doc.rows.map((row) => {
    const next = { ...row };
    delete next[columnId];
    return next;
  });
  const series = doc.encoding.series.filter((s) => s.columnId !== columnId);
  const x = doc.encoding.x === columnId ? columns[0].id : doc.encoding.x;
  return {
    ...doc,
    columns,
    rows,
    encoding: { x, series },
    source: demoteSource(doc),
  };
}

// §3.2.1 A rename touches the LABEL only. The id is what the encoding holds, which is the
// whole reason this cannot break a chart.
export function renameColumn(
  doc: ChartDocument,
  columnId: string,
  label: string,
): ChartDocument {
  return {
    ...doc,
    columns: doc.columns.map((c) => (c.id === columnId ? { ...c, label } : c)),
  };
}

export function setColumnType(
  doc: ChartDocument,
  columnId: string,
  type: ChartColumn["type"],
): ChartDocument {
  const columns = doc.columns.map((c) =>
    c.id === columnId ? { ...c, type } : c,
  );
  // Re-parse the column's cells against the new type rather than leaving strings in a column
  // now declared numeric: a chart reads the VALUES, not the declaration, so a column switched
  // to number that still holds strings plots nothing and says nothing about why.
  const rows =
    type === "number"
      ? doc.rows.map((row) => ({
          ...row,
          [columnId]: coerceNumber(row[columnId]),
        }))
      : doc.rows;
  return { ...doc, columns, rows };
}

function coerceNumber(value: string | number | null): string | number | null {
  if (typeof value === "number" || value == null) return value;
  const parsed = parseCell(value);
  return typeof parsed === "number" ? parsed : value;
}

// §4.2 A pasted block, expanding the table to fit. `atRow`/`atColumn` are where the paste
// landed, so pasting into the middle of a table overwrites from there rather than replacing
// everything — which is what a spreadsheet does and therefore what an author expects.
//
// New columns get generated labels rather than adopting the pasted first row as headers: this
// function cannot know whether the block includes a header, and guessing wrong renames the
// user's columns to their own data. Header adoption belongs to CSV import, which has a
// `header: true` flag to go on (§4.3).
export function pasteTable(
  doc: ChartDocument,
  block: string[][],
  atRow: number,
  atColumn: number,
): ChartDocument {
  if (block.length === 0) return doc;
  const width = Math.max(...block.map((r) => r.length));
  const columns = [...doc.columns];
  while (columns.length < atColumn + width) {
    columns.push({ ...newColumn(`Series ${columns.length}`), type: "number" });
  }
  const rows = doc.rows.map((row) => ({ ...row }));
  while (rows.length < atRow + block.length && rows.length < CHART_ROW_CAP) {
    rows.push(blankRow(columns));
  }
  block.forEach((line, r) => {
    const row = rows[atRow + r];
    if (!row) return; // clipped by the row cap
    line.forEach((cell, c) => {
      const column = columns[atColumn + c];
      if (column) row[column.id] = parseCell(cell);
    });
  });
  // Re-infer the type of every column the paste touched: a column seeded as "number" that was
  // pasted full of month names has to become text or the axis tries to scale it.
  const touched = new Set(
    columns.slice(atColumn, atColumn + width).map((c) => c.id),
  );
  const typed = columns.map((column) =>
    touched.has(column.id)
      ? { ...column, type: inferColumnType(rows.map((r) => r[column.id])) }
      : column,
  );
  return withEncodingRepaired(
    { ...doc, columns: typed, rows, source: demoteSource(doc) },
    doc,
  );
}

// === Encoding ==============================================================================

// Plotting every numeric column against the first non-numeric one is the encoding almost
// every table wants, and it is what makes a paste or an import draw something immediately
// rather than an empty frame with a panel to go and configure.
export function defaultEncoding(
  columns: ChartColumn[],
): ChartDocument["encoding"] {
  const x = columns.find((c) => c.type !== "number") ?? columns[0];
  const series = columns
    .filter((c) => c.id !== x?.id && c.type === "number")
    .map<ChartSeries>((c) => ({ columnId: c.id }));
  return {
    x: x?.id ?? "",
    // A table of nothing but numbers (the first column is then the X) still gets every other
    // column plotted; a table with no numeric column at all gets no series, and the block
    // says so rather than drawing axes around nothing.
    series:
      series.length > 0
        ? series
        : columns
            .filter((c) => c.id !== x?.id)
            .map<ChartSeries>((c) => ({ columnId: c.id })),
  };
}

// After an edit that changed the columns: keep the encoding the user has, drop what no longer
// exists, and adopt anything new that is plottable. Preserving `colorName` and per-series
// `type` is the point — re-deriving from scratch would silently reset a combo chart.
function withEncodingRepaired(
  next: ChartDocument,
  prev: ChartDocument,
): ChartDocument {
  const ids = new Set(next.columns.map((c) => c.id));
  const kept = prev.encoding.series.filter((s) => ids.has(s.columnId));
  const known = new Set(kept.map((s) => s.columnId));
  const x = ids.has(prev.encoding.x)
    ? prev.encoding.x
    : ((next.columns.find((c) => c.type !== "number") ?? next.columns[0])?.id ??
      "");
  const added = next.columns
    .filter((c) => c.id !== x && !known.has(c.id) && c.type === "number")
    .map<ChartSeries>((c) => ({ columnId: c.id }));
  return { ...next, encoding: { x, series: [...kept, ...added] } };
}

// === The render projection =================================================================

// Recharts wants an array of objects keyed by the dataKey it is given, so the column ids ARE
// the data keys and the projection is nearly the identity. Two things it does do:
//
//   • **the X value is stringified for a CATEGORICAL axis** — but not for a scatter's. A
//     numeric-looking category ("2024") is fine as a string on a `type="category"` axis and
//     wrong as a number: left numeric, Recharts scales the axis and spaces the bars by value
//     instead of drawing one per row. A scatter is the exact opposite case — its X is a
//     MEASUREMENT on a `type="number"` axis, and a stringified one plots nothing at all. So
//     the chart type decides, because the two axes want opposite things from the same column.
//     Getting it backwards is silent: the chart still draws, it is just wrong.
//   • a plotted cell that is not a number becomes `null`, so a stray "n/a" leaves a GAP
//     rather than being coerced to zero and drawing a dip that is not in the data.
export interface ChartDatum {
  [key: string]: string | number | null;
}

export function chartData(doc: ChartDocument): ChartDatum[] {
  const seriesIds = doc.encoding.series.map((s) => s.columnId);
  const numericX = doc.type === "scatter";
  return doc.rows.map((row) => {
    const rawX = row[doc.encoding.x];
    const datum: ChartDatum = {
      [doc.encoding.x]: numericX
        ? typeof rawX === "number"
          ? rawX
          : null
        : cellText(rawX),
    };
    for (const id of seriesIds) {
      const value = row[id];
      datum[id] = typeof value === "number" ? value : null;
    }
    return datum;
  });
}

export function columnLabel(doc: ChartDocument, columnId: string): string {
  return doc.columns.find((c) => c.id === columnId)?.label ?? columnId;
}

// §11.1 The text alternative every chart carries, so a screen reader is handed a sentence
// rather than several hundred `<path>` elements. Derived rather than authored: an alt text
// the user has to write is an alt text nobody writes.
export function chartAriaLabel(doc: ChartDocument): string {
  const names = doc.encoding.series.map((s) => columnLabel(doc, s.columnId));
  const kind = doc.type === "combo" ? "combination" : doc.type;
  const by = doc.encoding.x ? ` by ${columnLabel(doc, doc.encoding.x)}` : "";
  if (names.length === 0) return `Empty ${kind} chart`;
  return `${kind} chart of ${names.join(", ")}${by}, ${doc.rows.length} rows`;
}

// === The seed ==============================================================================

// §4.6 A new chart is a small, plausible table rather than an empty grid — the same call
// `griddSeed.ts` makes for a grid and `createGanttDoc` for a schedule. An empty widget on a
// slide reads as broken, and the first thing an author does is retype what is already there.
//
// Three categories × two series: enough for the legend, the axis and a grouped bar to all be
// visible at once, which is what tells the author what they are looking at.
export function createChartDoc(type: ChartType = "bar"): ChartDocument {
  const category = newColumn("Category");
  const first = { ...newColumn("This year"), type: "number" as const };
  const second = { ...newColumn("Last year"), type: "number" as const };
  const columns = [category, first, second];
  const values: [string, number, number][] = [
    ["Alpha", 42, 31],
    ["Beta", 58, 49],
    ["Gamma", 35, 44],
  ];
  return {
    version: 1,
    type,
    columns,
    rows: values.map(([label, a, b]) => ({
      [category.id]: label,
      [first.id]: a,
      [second.id]: b,
    })),
    source: { kind: "manual" },
    encoding: defaultEncoding(columns),
    // Nothing baked in at creation time that a later layout or sheet change could make wrong:
    // the look comes from the sheet on every render (§6.2), and the size from the window.
    options: {},
  };
}

// A doc read out of a file or a nugget may name a column the encoding lost, or predate a
// field. One defensive pass on the way into a renderer, so no component has to.
export function normalizeChartDoc(doc: ChartDocument): ChartDocument {
  const ids = new Set(doc.columns.map((c) => c.id));
  const series = (doc.encoding?.series ?? []).filter((s) =>
    ids.has(s.columnId),
  );
  const x = ids.has(doc.encoding?.x)
    ? doc.encoding.x
    : ((doc.columns.find((c) => c.type !== "number") ?? doc.columns[0])?.id ??
      "");
  if (
    x === doc.encoding?.x &&
    series.length === (doc.encoding?.series?.length ?? 0)
  ) {
    return doc;
  }
  return { ...doc, encoding: { x, series } };
}
